// Public page — request a password reset email.
// Always shows a generic success state to prevent email enumeration; the API
// returns 200 whether the email exists or not.

import { useState, type JSX, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { FormField } from "../components/FormField.js";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "") + "/hcm/api/v1";

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function ForgotPasswordPage(): JSX.Element {
  const [email,    setEmail]    = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok && res.status !== 200) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? "Could not send reset link. Please try again.");
        setLoading(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Unable to connect. Check your internet connection.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="text-2xl font-bold text-navy-700">HCM</span>
          <p className="mt-1 text-sm text-slate-500">Home Care Management</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Reset password</h1>

          {submitted ? (
            <>
              <p className="text-sm text-slate-600 mb-4">
                If an account exists for <strong>{email}</strong>, a reset link has been sent.
                The link expires in 1 hour.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Didn't get it? Check your spam folder, or try again in a few minutes.
              </p>
              <Link to="/login" className="block w-full text-center h-10 leading-10
                bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800">
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-6">
                Enter the email address tied to your account. We'll send a link to reset your password.
              </p>
              {error && (
                <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}
              <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
                <FormField
                  id="email"
                  type="email"
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@agency.com"
                  autoComplete="email"
                  required
                />
                <button
                  type="submit"
                  disabled={loading || email.trim().length === 0}
                  className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {loading ? "Sending…" : "Send reset link"}
                </button>
                <Link to="/login" className="block w-full text-center text-sm text-slate-500 hover:text-slate-700">
                  ← Back to sign in
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
